import type { ReactNode } from "react";
import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  CreditCard,
  type LucideIcon,
  Shield,
  Trash2,
  Users,
  Video,
} from "lucide-react";
import { AccentFeatureCard } from "~/components/patterns/accent-feature-card";
import { AccentRow } from "~/components/patterns/accent-row";
import {
  getHelpGroupAnchorId,
  resolveHelpGroups,
} from "~/config/help-center-groups";
import { getTherapyGuideAccent } from "~/config/vodic-kroz-psihoterapiju";
import { type SearchableArticle } from "~/lib/help-center-search";
import { CrisisLineAlert } from "./crisis-line-alert";

type TaskShortcut = {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
};

const TASK_SHORTCUT_ACCENT_IDS = [
  "osnove-psihoterapije",
  "kome-se-obracas",
  "sa-cime-terapija-pomaze",
  "cena-i-trajanje",
  "terapijski-pravci",
] as const;

const taskShortcuts: TaskShortcut[] = [
  {
    title: "Zakaži, pomeri ili otkaži termin",
    description: "Termini, potvrde, 24h pravilo i šta ako terapeut otkaže.",
    href: "/podrska/klijenti/zakazivanje-i-termini",
    icon: CalendarDays,
  },
  {
    title: "Plaćanje i povraćaj novca",
    description: "Cene, kartice, krediti, paketi i sporovi oko naplate.",
    href: "/podrska/klijenti/cene-i-placanje",
    icon: CreditCard,
  },
  {
    title: "Video sesija ne radi",
    description: "Kamera, mikrofon, browser, internet i propuštena sesija.",
    href: "/podrska/klijenti/tehnicki-problemi-na-sesiji",
    icon: Video,
  },
  {
    title: "Privatnost i bezbednost",
    description: "Poruke, snimanje sesija, podaci i pristup nalogu.",
    href: "/podrska/klijenti/privatnost-i-bezbednost",
    icon: Shield,
  },
  {
    title: "Izbor terapeuta",
    description: "Preporuke, filteri, profili i promena terapeuta.",
    href: "/podrska/klijenti/izbor-terapeuta",
    icon: Users,
  },
  {
    title: "Brisanje naloga i podataka",
    description: "Kako se briše nalog, šta ostaje i kako tražiš kopiju.",
    href: "/podrska/klijenti/brisanje-naloga-i-podataka",
    icon: Trash2,
  },
];

type FaqItem = {
  question: string;
  answer: string;
  href: string;
};

const firstSessionFaq: FaqItem[] = [
  {
    question: "Šta ako ne znam kog terapeuta da izaberem?",
    answer:
      "Možeš da koristiš preporuke ili samostalno filtriranje. Nijedan terapeut se ne dodeljuje automatski.",
    href: "/podrska/klijenti/izbor-terapeuta",
  },
  {
    question: "Da li moram nešto da pripremim pre prve sesije?",
    answer:
      "Ne. Dovoljno je da se pojaviš. Ako ti je lakše, možeš zapisati par tema koje želiš da spomeneš.",
    href: "/podrska/klijenti/vodic-kroz-prvu-sesiju",
  },
  {
    question: "Da li se sesije snimaju?",
    answer:
      "Ne. Platforma ne snima video sesije, a terapeuti ne smeju da ih snimaju preko platforme.",
    href: "/podrska/klijenti/privatnost-i-bezbednost",
  },
];

function FaqCard({ item }: { item: FaqItem }) {
  return (
    <Link
      href={item.href}
      className="group flex items-start gap-3 rounded-xl border border-border/80 bg-background p-4 transition-colors hover:border-brand/40 hover:bg-muted/50"
    >
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">
          {item.question}
        </span>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          {item.answer}
        </p>
      </div>
      <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export function ProspectLanding({
  articles,
}: {
  articles: SearchableArticle[];
}): ReactNode {
  const groups = resolveHelpGroups("client", articles);

  return (
    <div>
      <section aria-labelledby="popular-actions" className="bg-background">
        <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="mb-7 flex flex-col gap-2 md:mb-9 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-brand">Najčešće</p>
              <h2
                id="popular-actions"
                className="marketing-heading mt-2 text-2xl text-foreground sm:text-3xl"
              >
                Izaberi šta pokušavaš da uradiš
              </h2>
              <p className="mt-2 text-muted-foreground">
                30+ verifikovanih terapeuta, jasan izbor i bez automatskog
                dodeljivanja. Najbrži put do odgovora je kroz konkretan zadatak,
                ne kroz listu svih članaka.
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {taskShortcuts.map((task, index) => {
              const accentId =
                TASK_SHORTCUT_ACCENT_IDS[
                  index % TASK_SHORTCUT_ACCENT_IDS.length
                ] ?? TASK_SHORTCUT_ACCENT_IDS[0];

              return (
                <AccentFeatureCard
                  key={task.href}
                  href={task.href}
                  icon={task.icon}
                  title={task.title}
                  description={task.description}
                  accent={getTherapyGuideAccent(accentId)}
                  ctaLabel="Otvori"
                />
              );
            })}
          </div>
        </div>
      </section>

      <div className="divide-y divide-border">
        {groups.map((group, index) => {
          const anchorId = getHelpGroupAnchorId(group.definition);

          return (
            <section
              key={anchorId}
              id={anchorId}
              aria-labelledby={`${anchorId}-heading`}
              className={index % 2 === 0 ? "bg-secondary" : "bg-background"}
            >
              <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
                <div className="mb-7 max-w-2xl md:mb-9">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${group.accent.badge}`}
                  >
                    Pregledaj
                  </span>
                  <h2
                    id={`${anchorId}-heading`}
                    className="marketing-heading mt-3 text-2xl text-foreground sm:text-3xl"
                  >
                    {group.definition.title}
                  </h2>
                  <p className="mt-2 text-muted-foreground">
                    {group.definition.description}
                  </p>
                </div>
                <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {group.articles.map((article) => (
                    <AccentRow
                      key={article.slug}
                      href={article.slug}
                      icon={group.definition.icon}
                      title={article.title}
                      description={article.description}
                      accent={group.accent}
                    />
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section
        aria-labelledby="first-session"
        className="border-t bg-background"
      >
        <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-brand">Pre prve sesije</p>
            <h2
              id="first-session"
              className="marketing-heading mt-2 text-2xl text-foreground sm:text-3xl"
            >
              Tri odgovora koja ljudi najčešće traže pre zakazivanja
            </h2>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {firstSessionFaq.map((item) => (
              <FaqCard key={item.question} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t bg-muted/30">
        <div className="max-w-8xl container mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="marketing-heading text-2xl text-foreground sm:text-3xl">
              Nisi pronašao odgovor?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Postavi pitanje za brz odgovor ili piši ljudskoj podršci za
              osetljivije teme.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href="/asistent"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-brand-action px-8 text-base font-medium text-white transition-colors hover:bg-brand-action-hover"
              >
                Postavi pitanje
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="/podrska/kontakt"
                className="inline-flex h-11 items-center justify-center rounded-full border border-input bg-background px-8 text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                Piši podršci
              </Link>
            </div>
            <div className="mt-8">
              <CrisisLineAlert />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
